import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Spinner, Alert, Table,
    Form, Badge, Pagination, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { formatCurrency, formatNumber } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function DepensesAchats() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [annees, setAnnees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [periode, setPeriode] = useState('mois');
    const [annee, setAnnee] = useState(new Date().getFullYear());
    const [itemsPerPage, setItemsPerPage] = useState(12);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'montant', direction: 'desc' });

    useEffect(() => {
        fetchAnnees();
        fetchData();
    }, [periode, annee]);

    const fetchAnnees = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/stats.php?action=annees_achats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setAnnees(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement années", err);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/stats.php?action=depenses_achats&periode=${periode}&annee=${annee}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setData(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const mois = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];

    const getPeriodeIcon = () => {
        return periode === 'mois' ? 'ri-calendar-line' : 'ri-calendar-2-line';
    };

    // Tri des données
    const sortedData = React.useMemo(() => {
        const sortableData = [...data];
        if (sortConfig.key) {
            sortableData.sort((a, b) => {
                const aVal = parseFloat(a[sortConfig.key]) || 0;
                const bVal = parseFloat(b[sortConfig.key]) || 0;
                
                if (aVal < bVal) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aVal > bVal) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableData;
    }, [data, sortConfig]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedData.length / itemsPerPage);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        document.querySelector('.table-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const totalDepenses = sortedData.reduce((sum, item) => sum + (parseFloat(item.montant) || 0), 0);
    const totalCommandes = sortedData.reduce((sum, item) => sum + (parseInt(item.nb_commandes) || 0), 0);
    const moyenneMensuelle = sortedData.length > 0 ? totalDepenses / sortedData.length : 0;

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement des dépenses...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button variant="outline-danger" className="mt-3" onClick={fetchData}>
                            <i className="ri-refresh-line me-2"></i>Réessayer
                        </Button>
                    </Alert>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(217, 119, 6, 0.15))',
                                            boxShadow: '0 10px 20px rgba(245, 158, 11, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-shopping-cart-2-line text-warning" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1">Dépenses achats</h1>
                                        <p className="text-muted mb-0">Analyse des achats fournisseurs</p>
                                    </div>
                                </div>
                                <Button variant="outline-secondary" onClick={() => navigate('/stats/dashboard')}>
                                    <i className="ri-arrow-left-line me-2"></i>Retour
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Filtres */}
                    <Card className="filter-card border-0 shadow-sm mb-4">
                        <Card.Body>
                            <Row className="g-3 align-items-end">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className={`${getPeriodeIcon()} me-2`}></i>
                                            Période
                                        </Form.Label>
                                        <Form.Select 
                                            value={periode} 
                                            onChange={(e) => {
                                                setPeriode(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                            className="filter-select py-2"
                                        >
                                            <option value="mois">📅 Mensuel</option>
                                            <option value="annee">📊 Annuel</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                {periode === 'mois' && (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold text-muted small text-uppercase">
                                                <i className="ri-calendar-2-line me-2"></i>
                                                Année
                                            </Form.Label>
                                            <Form.Select 
                                                value={annee} 
                                                onChange={(e) => setAnnee(e.target.value)}
                                                className="filter-select py-2"
                                            >
                                                {annees.map(a => (
                                                    <option key={a} value={a}>{a}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                )}
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-muted small text-uppercase">
                                            <i className="ri-list-settings-line me-2"></i>
                                            Affichage
                                        </Form.Label>
                                        <Form.Select 
                                            value={itemsPerPage} 
                                            onChange={(e) => {
                                                setItemsPerPage(parseInt(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="filter-select py-2"
                                        >
                                            <option value="6">6 par page</option>
                                            <option value="12">12 par page</option>
                                            <option value="24">24 par page</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Button 
                                        variant="primary" 
                                        onClick={fetchData}
                                        className="w-100 py-2"
                                        style={{
                                            background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                            border: 'none',
                                            borderRadius: '10px'
                                        }}
                                    >
                                        <i className="ri-refresh-line me-2"></i>Actualiser
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Cartes de résumé */}
                    <Row className="g-3 mb-4 stats-row">
                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3 d-flex align-items-center">
                                    <div className="stat-icon-wrapper bg-warning-soft me-3">
                                        <i className="ri-money-euro-circle-line text-warning fs-4"></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                                            Total dépenses
                                        </small>
                                        <h3 className="mb-0 fw-bold text-warning">{formatCurrency(totalDepenses)}</h3>
                                        <small className="text-muted">cumul période</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3 d-flex align-items-center">
                                    <div className="stat-icon-wrapper bg-info-soft me-3">
                                        <i className="ri-file-list-line text-info fs-4"></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                                            Nombre commandes
                                        </small>
                                        <h3 className="mb-0 fw-bold text-info">{formatNumber(totalCommandes)}</h3>
                                        <small className="text-muted">commandes passées</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={4}>
                            <Card className="border-0 shadow-sm h-100 stat-card">
                                <Card.Body className="p-3 d-flex align-items-center">
                                    <div className="stat-icon-wrapper bg-success-soft me-3">
                                        <i className="ri-calculator-line text-success fs-4"></i>
                                    </div>
                                    <div>
                                        <small className="text-muted d-block text-uppercase fw-semibold tracking-wide">
                                            Moyenne mensuelle
                                        </small>
                                        <h3 className="mb-0 fw-bold text-success">{formatCurrency(moyenneMensuelle)}</h3>
                                        <small className="text-muted">par mois</small>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Tableau */}
                    <Card className="border-0 shadow-sm table-card">
                        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="ri-file-list-line me-2 text-warning"></i>
                                Détail {periode === 'mois' ? 'mensuel' : 'annuel'}
                            </h5>
                            <Badge bg="warning" className="px-3 py-2">
                                {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, sortedData.length)} sur {sortedData.length}
                            </Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort(periode === 'mois' ? 'mois' : 'annee')}>
                                                Période <i className={`ri-arrow-${sortConfig.key === (periode === 'mois' ? 'mois' : 'annee') && sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                            </th>
                                            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => handleSort('montant')}>
                                                Montant <i className={`ri-arrow-${sortConfig.key === 'montant' && sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                            </th>
                                            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => handleSort('nb_commandes')}>
                                                Commandes <i className={`ri-arrow-${sortConfig.key === 'nb_commandes' && sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                            </th>
                                            <th className="text-end" style={{ cursor: 'pointer' }} onClick={() => handleSort('montant_moyen')}>
                                                Montant moyen <i className={`ri-arrow-${sortConfig.key === 'montant_moyen' && sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line ms-1`}></i>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((item, index) => {
                                            const libelle = periode === 'mois' 
                                                ? mois[item.mois - 1] 
                                                : item.annee;
                                            return (
                                                <tr key={index} style={{
                                                    animation: `fadeIn 0.3s ease ${index * 0.05}s both`
                                                }}>
                                                    <td className="fw-semibold text-capitalize">{libelle}</td>
                                                    <td className="text-end fw-bold text-warning">{formatCurrency(item.montant)}</td>
                                                    <td className="text-end">
                                                        <Badge bg="info" className="rounded-pill px-3 py-2">
                                                            {item.nb_commandes}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-end text-success fw-semibold">
                                                        {formatCurrency(item.montant_moyen || 0)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Card.Footer className="bg-white py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="text-muted small">
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, sortedData.length)} sur {sortedData.length} périodes
                                    </div>
                                    
                                    <Pagination className="mb-0">
                                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        
                                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) pageNum = i + 1;
                                            else if (currentPage <= 3) pageNum = i + 1;
                                            else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                            else pageNum = currentPage - 2 + i;
                                            
                                            return (
                                                <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => handlePageChange(pageNum)}>
                                                    {pageNum}
                                                </Pagination.Item>
                                            );
                                        })}
                                        
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                        <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            </Card.Footer>
                        )}
                    </Card>

                    {/* Pied de tableau */}
                    {sortedData.length > 0 && (
                        <Card className="border-0 shadow-sm mt-3 bg-light">
                            <Card.Body className="p-3">
                                <Row className="align-items-center">
                                    <Col md={6}>
                                        <span className="text-muted">
                                            <i className="ri-calculator-line me-2"></i>
                                            Total général
                                        </span>
                                    </Col>
                                    <Col md={6}>
                                        <div className="d-flex justify-content-end gap-4">
                                            <span className="fw-bold text-warning">{formatCurrency(totalDepenses)}</span>
                                            <span className="fw-bold text-info">{formatNumber(totalCommandes)}</span>
                                            <span className="fw-bold text-success">{formatCurrency(moyenneMensuelle)}</span>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}
                </div>

                <style>{`
                    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                    .fade-in { animation: fadeIn 0.5s ease; }
                    .stats-row .stat-card { transition: all 0.3s ease; border-radius: 16px; }
                    .stats-row .stat-card:hover { transform: translateY(-5px); box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1) !important; }
                    .stat-icon-wrapper { width: 56px; height: 56px; display: flex; align-items: center; justify-content: center; border-radius: 16px; transition: transform 0.3s ease; }
                    .stat-card:hover .stat-icon-wrapper { transform: scale(1.1); }
                    .filter-card { border-radius: 16px; background: #f8fafc; }
                    .filter-select { border-radius: 10px; border: 1px solid #e2e8f0; transition: all 0.2s ease; }
                    .filter-select:focus { border-color: #4361ee; box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.1); }
                    .table-card { border-radius: 16px; overflow: hidden; }
                    .custom-table thead th { background: #f8fafc; color: #1e293b; font-weight: 600; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; padding: 1rem 0.75rem; }
                    .custom-table thead th:hover { color: #4361ee; background: #f1f5f9; }
                    .custom-table tbody td { padding: 1rem 0.75rem; vertical-align: middle; }
                    .custom-table tbody tr { transition: all 0.2s ease; }
                    .custom-table tbody tr:hover { background: #f1f5f9 !important; }
                    .bg-warning-soft { background: rgba(245, 158, 11, 0.1); }
                    .bg-info-soft { background: rgba(13, 202, 240, 0.1); }
                    .bg-success-soft { background: rgba(16, 185, 129, 0.1); }
                    .tracking-wide { letter-spacing: 0.5px; }
                    .pagination .page-link { border-radius: 8px; margin: 0 2px; color: #4361ee; border: 1px solid #e2e8f0; }
                    .pagination .page-link:hover { background: #f1f5f9; color: #3a56d4; border-color: #cbd5e1; }
                    .pagination .active .page-link { background: linear-gradient(135deg, #4361ee, #3a56d4); border-color: #4361ee; color: white; }
                `}</style>

                <Footer />
            </div>
        </>
    );
}